import { useEffect, useState } from "react";
import { Copy, Check, AlertTriangle, Landmark } from "lucide-react";
import { api, errMsg, fmtMoney } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

function CopyRow({ label, value, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (e) { toast.error("Kopyalanamadı"); }
  };
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl bg-slate-950/70 border border-slate-800 px-4 py-3">
      <div className="min-w-0">
        <p className="text-[10px] font-mono-cx uppercase tracking-widest text-slate-500">{label}</p>
        <p className="font-mono-cx text-sm text-slate-100 truncate" data-testid={testId}>{value}</p>
      </div>
      <button data-testid={`${testId}-copy`} onClick={copy} className="h-9 w-9 rounded-lg bg-slate-900 border border-slate-800 hover:border-cyan-500/50 flex items-center justify-center text-cyan-300 shrink-0 transition-colors">
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}

export function PaymentDialog({ open, onOpenChange, info, onSubmitted }) {
  const [pay, setPay] = useState(null);
  const [warn, setWarn] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !info) return;
    setWarn(false);
    api.get(`/infos/${info.id}/payment-info`).then((r) => setPay(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [open, info]);

  const confirm = async () => {
    setLoading(true);
    try {
      await api.post("/payments", { info_id: info.id });
      toast.success("Ödeme bildirimin alındı. Admin onayladığında içerik açılır.");
      onOpenChange(false);
      onSubmitted?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-950/95 backdrop-blur-xl border-slate-800 text-slate-100 sm:max-w-lg" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Landmark className="h-5 w-5 text-cyan-400" /> Havale / EFT ile Satın Al</DialogTitle>
          <DialogDescription className="text-slate-400">Aşağıdaki IBAN'a tutarı gönder, sonra "Gönderdim" butonuna bas.</DialogDescription>
        </DialogHeader>
        {!pay ? (
          <div className="h-40 rounded-xl bg-slate-900/60 animate-pulse" />
        ) : (
          <div className="space-y-3">
            <CopyRow label="IBAN" value={pay.iban} testId="iban-value" />
            <CopyRow label="Alıcı Ad Soyad" value={pay.iban_name} testId="iban-name-value" />
            <div className="rounded-xl bg-slate-950/70 border border-slate-800 px-4 py-3">
              <p className="text-[10px] font-mono-cx uppercase tracking-widest text-slate-500">Tutar</p>
              <p className="font-display text-xl font-bold text-cyan-300" data-testid="payment-amount">{fmtMoney(pay.amount, pay.currency)}</p>
            </div>

            {warn ? (
              <div data-testid="warning-ban-alert" className="cx-shake rounded-xl border-2 border-red-500 bg-red-950/90 p-5 text-red-100 shadow-[0_0_40px_-10px_rgba(239,68,68,0.8)]">
                <p className="font-display font-extrabold text-lg flex items-center gap-2 text-red-200"><AlertTriangle className="h-5 w-5 cx-pulse-red rounded-full" /> DİKKAT!</p>
        <p className="text-sm mt-2 leading-relaxed font-semibold">{pay.ban_warning}</p>
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <button data-testid="payment-cancel-button" onClick={() => setWarn(false)} className="h-11 rounded-xl bg-slate-900 border border-slate-700 text-slate-200 text-sm font-semibold hover:border-slate-500 transition-colors">Vazgeç</button>
                  <button data-testid="payment-confirm-button" onClick={confirm} disabled={loading} className="h-11 rounded-xl bg-red-600 hover:bg-red-500 text-white text-sm font-bold transition-colors">{loading ? "Gönderiliyor..." : "Evet, ödemeyi gönderdim"}</button>
                </div>
              </div>
            ) : (
              <button data-testid="payment-sent-button" onClick={() => setWarn(true)} className="w-full h-12 rounded-xl cx-btn-primary text-sm">GÖNDERDİM</button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
